import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Upload, Users, Wallet } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Skeleton,
  useToast,
  CopyFeedbackIcon,
} from '@shared/ui';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { useAuth } from '@entities/auth';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import type { AssetBalanceDto } from '@shared/api/contracts';
import { useRefreshReferralCode } from '@features/referral';
import { useWithdrawalsLocked, WithdrawalModal, WithdrawalsLockBanner } from '@features/withdrawal';
import { landingHref } from '@shared/i18n/lib/landing-href';
import { BalanceMetricsGrid } from '@widgets/balance/balance-metrics-grid';

interface ReferralOverviewPanelProps {
  balance: number;
  reserved?: string | null;
  available?: string | null;
  assetBalances: AssetBalanceDto[];
  partnersCount: number;
  balanceError?: unknown;
  countError?: unknown;
  isOverviewLoading?: boolean;
  isBalanceValidating?: boolean;
  onRetryOverview?: () => void;
}

export function ReferralOverviewPanel({
  balance,
  reserved = null,
  available = null,
  assetBalances,
  partnersCount,
  balanceError,
  countError,
  isOverviewLoading = false,
  isBalanceValidating = false,
  onRetryOverview,
}: ReferralOverviewPanelProps) {
  const { t, i18n } = useTranslation('cabinet');
  const { user } = useAuth();
  const { copy, isCopied } = useCopyToClipboard();
  const { toast } = useToast();
  const { refreshReferralCode, isLoading: isRefreshing } = useRefreshReferralCode();
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);
  const withdrawalsLocked = useWithdrawalsLocked();

  const referralCode = user?.referralCode ?? '';
  const referralLink = landingHref(i18n.language, referralCode);

  const handleCopyCode = async () => {
    if (!referralCode) return;
    await copy(referralCode, t('referral.codeCopied'), 'referral-code');
  };

  const handleCopyLink = async () => {
    await copy(referralLink, t('referral.linkCopied'), 'referral-link');
  };

  const handleRefresh = async () => {
    const result = await refreshReferralCode();
    if (result.success) {
      toast(t('referral.codeRefreshed'), 'success');
    }
  };

  return (
    <>
      <div className="surface-card overflow-hidden p-0">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="section-title">{t('referral.overviewTitle')}</h2>
            <p className="section-description mt-0.5">{t('referral.overviewDescription')}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-10 w-full shrink-0 sm:w-auto"
            onClick={() => void handleRefresh()}
            loading={isRefreshing}
            leftIcon={<RefreshCw className="size-4" />}
          >
            {t('referral.refreshCode')}
          </Button>
        </div>

        <div className="grid gap-0 lg:grid-cols-2">
          <section className="flex flex-col justify-between gap-4 border-b border-border p-4 sm:gap-6 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary sm:h-12 sm:w-12 sm:rounded-2xl">
                  <Wallet className="h-5 w-5 text-muted-foreground sm:h-6 sm:w-6" />
                </div>
                <h3 className="font-sans text-base font-semibold text-base-foreground sm:text-lg">{t('balance.label')}</h3>
              </div>

              {balanceError ? (
                <div className="space-y-2">
                  <p className="font-sans text-sm text-destructive">
                    {getApiErrorMessage(balanceError, t, 'referral.errors.balanceLoad')}
                  </p>
                  <Button variant="outline" size="sm" onClick={onRetryOverview}>
                    {t('common.retry', { ns: 'common' })}
                  </Button>
                </div>
              ) : (
                <BalanceMetricsGrid
                  available={available}
                  reserved={reserved}
                  total={balance}
                  isLoading={isOverviewLoading}
                />
              )}

              {countError ? (
                <p className="font-sans text-xs text-destructive sm:text-sm">
                  {getApiErrorMessage(countError, t, 'referral.errors.partnersCountLoad')}
                </p>
              ) : isOverviewLoading ? (
                <Skeleton className="h-7 w-48 rounded-full" />
              ) : (
                <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  {t('referral.partnersCount', { count: partnersCount })}
                </div>
              )}
            </div>

            <WithdrawalsLockBanner />
            <Button
              variant="default"
              className="page-action-button w-full sm:w-auto sm:self-start"
              onClick={() => setIsWithdrawalOpen(true)}
              disabled={Boolean(balanceError) || isOverviewLoading || withdrawalsLocked}
            >
              <Upload className="mr-2 h-4 w-4" />
              {t('withdrawal.withdraw')}
            </Button>
          </section>

          <section className="space-y-4 p-4 sm:p-6">
            <div className="space-y-2">
              <Label>{t('referral.codeLabel')}</Label>
              <div className="flex gap-2">
                <Input value={referralCode} readOnly className="min-w-0 flex-1 font-mono text-sm" />
                <Button variant="outline" size="icon-sm" className="shrink-0 sm:size-10" onClick={() => void handleCopyCode()}>
                  <CopyFeedbackIcon copied={isCopied('referral-code')} className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('referral.linkLabel')}</Label>
              <div className="flex gap-2">
                <Input value={referralLink} readOnly className="min-w-0 flex-1 text-sm" />
                <Button variant="outline" size="icon-sm" className="shrink-0 sm:size-10" onClick={() => void handleCopyLink()}>
                  <CopyFeedbackIcon copied={isCopied('referral-link')} className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>

      <WithdrawalModal
        open={isWithdrawalOpen}
        onOpenChange={setIsWithdrawalOpen}
        type="partners"
        assetBalances={assetBalances}
        walletBalance={available == null ? undefined : Number(available)}
        isBalanceLoading={isOverviewLoading || isBalanceValidating}
        balanceError={balanceError}
        onRetryBalance={onRetryOverview}
      />
    </>
  );
}
